import { ArrowDownUp, BarChart3, TrendingUp } from "lucide-react";
import { ContentCard } from "@/components/ui/ContentCard";

export type AnalyticsData = {
  total: number;
  closed: number;
  open: number;
  last30: number;
  resolutionRate: number;
  byGrade: Record<string, number>;
  byPath: Record<string, number>;
  avgPerMonth: number;
  parentContactCount: number;
};

type Props = {
  analytics: AnalyticsData;
};

export const IncidentAnalytics = ({ analytics }: Props) => {
  return (
    <ContentCard>
      <h2 className="text-xl lg:text-2xl font-semibold text-[#248f84] font-literata mb-4">
        Incident Overview
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-50 rounded-xl p-4 text-center border border-gray-100">
          <div className="text-2xl font-bold text-gray-800">{analytics.total}</div>
          <div className="text-xs text-gray-500 mt-1">Total Incidents</div>
        </div>
        <div className="bg-green-50 rounded-xl p-4 text-center border border-green-100">
          <div className="text-2xl font-bold text-green-700">{analytics.closed}</div>
          <div className="text-xs text-green-600 mt-1">Resolved</div>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 text-center border border-amber-100">
          <div className="text-2xl font-bold text-amber-700">{analytics.open}</div>
          <div className="text-xs text-amber-600 mt-1">Open</div>
        </div>
        <div className="bg-bodhi-orange/5 rounded-xl p-4 text-center border border-bodhi-orange/10">
          <div className="text-2xl font-bold text-bodhi-orange">{analytics.resolutionRate}%</div>
          <div className="text-xs text-gray-500 mt-1">Resolution Rate</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-bodhi-orange" />
            <span className="text-sm font-semibold text-gray-700">By Grade Level</span>
          </div>
          {Object.keys(analytics.byGrade).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(analytics.byGrade)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([grade, count]) => (
                  <div key={grade} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Grade {grade}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-bodhi-teal rounded-full"
                          style={{ width: `${(count / analytics.total) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 w-5 text-right">{count}</span>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400">No data yet</p>
          )}
        </div>

        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <ArrowDownUp className="w-4 h-4 text-bodhi-orange" />
            <span className="text-sm font-semibold text-gray-700">Resolution Path</span>
          </div>
          {Object.keys(analytics.byPath).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(analytics.byPath).map(([path, count]) => (
                <div key={path} className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">{path}</span>
                  <span className="text-xs font-medium text-gray-700">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400">No data yet</p>
          )}
        </div>

        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-bodhi-orange" />
            <span className="text-sm font-semibold text-gray-700">Trends</span>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">Last 30 days</span>
              <span className="text-xs font-medium text-gray-700">
                {analytics.last30} incident{analytics.last30 !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">Avg. per month</span>
              <span className="text-xs font-medium text-gray-700">{analytics.avgPerMonth}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">Parent contact made</span>
              <span className="text-xs font-medium text-gray-700">{analytics.parentContactCount}</span>
            </div>
          </div>
        </div>
      </div>
    </ContentCard>
  );
};
